import React, { useState } from 'react';
import { DateFilter } from '../../../../components/DateRangePicker';
import { Button, Col, Row } from 'reactstrap';
import { LabelledDatePicker } from '../../../../components/LabelledDatePicker';
import classNames from 'classnames';
import styles from './styles.module.scss';
import { SelectOption } from '../../../../components/Select';
import { LabelledSelect } from '../../../../components/LabelledSelect';
import { Job } from '../../../../entities/v1/applicant_tracking/Job';
import { Location } from '../../../../entities/v1/applicant_tracking/Location';
import {
  FilterResult,
  MultiSelectAsyncFilter,
} from '../../../../components/MultiSelectAsyncFilter';
import { mapResultToSelectOption } from '../../../../utils/mapResultToSelectOption';
import { DepartmentService } from '../../../../services/v1/applicant_tracking/DepartmentService';
import { JobService } from '../../../../services/v1/applicant_tracking/JobService';
import { LocationService } from '../../../../services/v1/applicant_tracking/LocationService';
import { PlanTemplateService } from '../../../../services/v1/applicant_tracking/PlanTemplateService';
import { AvailableStagesService } from '../../../../services/applicant_tracking/analytics/AvailableStagesService';

export const DEFAULT_FILTER_BY: SelectOption = {
  label: 'Departments',
  value: 'department',
};

export const FILTER_BY_OPTIONS: SelectOption[] = [
  DEFAULT_FILTER_BY,
  {
    label: 'Jobs',
    value: 'job',
  },
  {
    label: 'Workflows',
    value: 'workflow',
  },
];

export interface FilterData {
  filterBys: SelectOption[];
  jobs: Job[];
  locations: Location[];
}

export interface FilterState {
  filterBy: SelectOption;
  entries: SelectOption[];
  locations: SelectOption[];
  stageIds: SelectOption[];
  dateRange: DateFilter;
}

interface PropTypes {
  filterState: FilterState;
  onFilterUpdate: (
    name: keyof FilterState,
    value: FilterState[keyof FilterState],
  ) => void;
  onApplyClick: () => void;
  onResetClick: () => void;
}

interface FiltersConfig {
  [key: string]: {
    label: string;
    load: (search: string, page: number) => Promise<FilterResult>;
  };
}

const filtersConfig: FiltersConfig = {
  department: {
    label: 'Departments',
    load: (name: string, page: number) =>
      mapResultToSelectOption(
        DepartmentService.list({ name, page }),
        (filter) => filter.departments,
      ),
  },
  job: {
    label: 'Jobs',
    load: (name: string, page: number) =>
      mapResultToSelectOption(
        JobService.list({ name, page }),
        (filter) => filter.jobs,
      ),
  },
  workflow: {
    label: 'Workflows',
    load: (_search: string, _page: number) =>
      PlanTemplateService.list({ publishState: 'published' }).then(
        (result) => ({
          options: result.planTemplates.map((pt) => ({
            value: String(pt.id),
            label: pt.name,
          })),
          page: 1,
          pageCount: 1,
          entriesCount: result.planTemplates.length,
        }),
      ),
  },
};

function DynamicDataMultiSelectAsyncFilter(props: PropTypes) {
  const filterBy = props.filterState.filterBy;

  return (
    <MultiSelectAsyncFilter
      isMulti
      label={filtersConfig[filterBy.value]?.label.slice(0, -1)}
      loadOptions={filtersConfig[filterBy.value]?.load}
      onChange={(newValues) => props.onFilterUpdate('entries', newValues)}
      selected={props.filterState.entries}
      max={10}
    />
  );
}

export function Filters(props: PropTypes) {
  const [fixedStageIds, setFixedStageIds] = useState<Set<string>>(new Set());

  const stagesKey = JSON.stringify({
    filterBy: props.filterState.filterBy,
    entries: props.filterState.entries,
  });

  function loadStageOptions(
    _search: string,
    page: number,
  ): Promise<FilterResult> {
    if (page > 1) {
      return Promise.resolve({
        options: [],
        page: page,
        pageCount: 1,
        entriesCount: 0,
      });
    }

    return AvailableStagesService.list({
      filterBy: props.filterState.filterBy.value,
      filterByIds: props.filterState.entries.map((e) => e.value),
    }).then((result) => {
      const newFixedIds = new Set<string>();

      result.stages.forEach((stage) => {
        if (stage.isFirst || stage.isHired) {
          newFixedIds.add(String(stage.id));
        }
      });

      setFixedStageIds(newFixedIds);

      const fixedOptions = result.stages
        .filter((s) => newFixedIds.has(String(s.id)))
        .map((s) => ({ value: String(s.id), label: s.name }));

      const currentStageIds = new Set(
        props.filterState.stageIds.map((s) => s.value),
      );
      const missingFixed = fixedOptions.filter(
        (opt) => !currentStageIds.has(opt.value),
      );

      if (missingFixed.length > 0) {
        props.onFilterUpdate('stageIds', [
          ...props.filterState.stageIds,
          ...missingFixed,
        ]);
      }

      return {
        options: result.stages.map((s) => ({
          value: String(s.id),
          label: s.name,
        })),
        page: 1,
        pageCount: 1,
        entriesCount: result.stages.length,
      };
    });
  }

  function handleStageIdsChange(newValues: SelectOption[]) {
    const enforced = newValues.filter((v) => !fixedStageIds.has(v.value));

    const fixedOptions = props.filterState.stageIds.filter((v) =>
      fixedStageIds.has(v.value),
    );

    props.onFilterUpdate('stageIds', [...fixedOptions, ...enforced]);
  }

  return (
    <div className='mb-3 w-100'>
      <Row className='g-2 fs-5 h-100'>
        <Col xs='12' md='4' xl='2'>
          <LabelledSelect
            options={FILTER_BY_OPTIONS}
            selected={props.filterState.filterBy}
            onChange={(newFilter) =>
              props.onFilterUpdate('filterBy', newFilter)
            }
            label='Filter By'
            className={'w-100'}
          />
        </Col>
        <Col xs='12' md='4' xl='2'>
          <DynamicDataMultiSelectAsyncFilter {...props} />
        </Col>
        <Col xs='12' md='4' xl='2'>
          <MultiSelectAsyncFilter
            isMulti
            label={'Locations'}
            loadOptions={(name: string, page: number) =>
              mapResultToSelectOption(
                LocationService.list({ name, page }),
                (filter) => filter.locations,
              )
            }
            onChange={(newValues) =>
              props.onFilterUpdate('locations', newValues)
            }
            selected={props.filterState.locations ?? []}
            max={10}
          />
        </Col>
        <Col xs='12' md='4' xl='2'>
          <MultiSelectAsyncFilter
            key={stagesKey}
            isMulti
            label={'Stages'}
            loadOptions={loadStageOptions}
            onChange={handleStageIdsChange}
            selected={props.filterState.stageIds ?? []}
          />
        </Col>
        <Col xs='12' md='4' xl='2'>
          <LabelledDatePicker
            label='Date'
            dateFilter={props.filterState.dateRange}
            onChange={(start, end, range) =>
              props.onFilterUpdate('dateRange', { start, end, range })
            }
            className={'w-100'}
          />
        </Col>
        <Col
          xs='6'
          md='4'
          xl='2'
          className={classNames(
            'd-flex text-nowrap align-bottom align-self-end',
            styles['filter-buttons-column'],
          )}
        >
          <div
            className={classNames(
              'd-flex gap-3 justify-content-between p-0',
              styles['filter-buttons'],
            )}
          >
            <Button
              type='button'
              color='secondary'
              onClick={props.onResetClick}
            >
              Reset
            </Button>
            <Button type='button' color='primary' onClick={props.onApplyClick}>
              Apply
            </Button>
          </div>
        </Col>
      </Row>
    </div>
  );
}
